import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import PlayerProfile from '../components/PlayerProfile';
import sanityClient from '../lib/sanityClient';
import SEO from '../components/SEO';

function PlayerPage() {
  const { id } = useParams();
  const [player, setPlayer] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!id) {
      setError('No player ID provided');
      setIsLoading(false);
      return;
    }

    sanityClient
      .fetch(
        `*[_type == "player" && slug.current == $id][0]{
          name,
          number,
          position,
          positionGroup,
          canPlayGoalie,
          canPlaySkater,
          height,
          weight,
          birthdate,
          hometown,
          "imageUrl": image.asset->url,
          stats,
          highlights,
          captainStatus,
          bio,
          seasonStats[] {
            _key,
            "seasonName": season->name,
            "seasonId": season._ref,
            statsType,
            gp,
            g,
            a,
            pts,
            pim,
            ppg,
            ppa,
            shg,
            otg,
            eng,
            sa,
            ga,
            sv,
            svp,
            gaa,
            mins
          }
        }`,
        { id }
      )
      .then((data) => {
        if (!data) {
          setError('Player not found');
        } else {
          // Format the data
          let formattedPlayer = {
            ...data,
            birthdate: data.birthdate
              ? (() => {
                  const [year, month, day] = data.birthdate.split('-').map(Number);
                  return new Date(year, month - 1, day).toLocaleDateString();
                })()
              : 'N/A',
            captainStatus: data.captainStatus || 'none',
            canPlayGoalie: data.canPlayGoalie || false,
            canPlaySkater: data.canPlaySkater || false,
            // Sort season stats by season name and stats type (most recent first, skater stats before goalie stats)
            seasonStats: data.seasonStats
              ? data.seasonStats.sort((a, b) => {
                  // First sort by season name (most recent first)
                  if (a.seasonName && b.seasonName && a.seasonName !== b.seasonName) {
                    return b.seasonName.localeCompare(a.seasonName);
                  }
                  // Then sort by stats type (skater before goalie within same season)
                  if (a.seasonName === b.seasonName) {
                    const typeA = a.statsType || 'skater';
                    const typeB = b.statsType || 'skater';
                    if (typeA !== typeB) {
                      return typeA === 'skater' ? -1 : 1;
                    }
                  }
                  return 0;
                })
              : [],
          };

          setPlayer(formattedPlayer);
        }
        setIsLoading(false);
      })
      .catch((err) => {
        console.error('Error fetching player:', err);
        setError('Failed to load player data. Please try again later.');
        setIsLoading(false);
      });
  }, [id]);

  if (isLoading) {
    return (
      <>
        <SEO
          title="Loading Player - Bombardiers Hockey"
          description="Loading player information for the Bombardiers Hockey team."
        />
        <div className="bg-black min-h-screen flex justify-center items-center">
          <div className="text-white text-xl">Loading player data...</div>
        </div>
      </>
    );
  }

  if (error || !player) {
    return (
      <>
        <SEO
          title="Player Not Found - Bombardiers Hockey"
          description="The requested player could not be found."
        />
        <div className="bg-black min-h-screen flex justify-center items-center">
          <div className="bg-gray-900 p-6 rounded-lg text-white text-xl">
            {error || 'Player not found'}
          </div>
        </div>
      </>
    );
  }

  // Create dynamic SEO based on player data
  const playerSEO = {
    title: `${player.name} #${player.number} - Bombardiers Hockey`,
    description: `View ${player.name}'s profile, statistics, and career highlights. ${player.position} for the Bombardiers Hockey team.${player.canPlayGoalie && player.positionGroup !== 'Goalie' ? ' Also plays goalie.' : ''}${player.canPlaySkater && player.positionGroup === 'Goalie' ? ' Also plays skater positions.' : ''}`,
    image: player.imageUrl || '/bomb-puck-logo.png',
  };

  return (
    <>
      <SEO {...playerSEO} />
      <PlayerProfile player={player} />
    </>
  );
}

export default PlayerPage;
